import { Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DropdownController } from "../../Core/Dropdown/Dropdown";
import { Pool } from "../../../models/Organisation";
import Subsidiary from "../../../models/Subsidiary";

type Option = { key: string; name: string };

/**
 * Sentinel value for the "no subsidiary" option when the selector is rendered
 * with a Radix-based `AppSelect` (which forbids an empty-string item value —
 * it reserves `""` for the cleared/placeholder state). On submit this sentinel
 * maps back to "no subsidiary" (undefined/null). The old Chakra
 * `DropdownController` below tolerates an empty string, so it keeps using `""`.
 */
export const ORGANISATION_DIRECTLY_VALUE = "__organisation_directly__";

interface SubsidiarySelectProps {
	control: Control<any, any>;
	name: string;
	subsidiaries: Subsidiary[];
	margin?: string;
	mandatory?: boolean;
}

/**
 * Dropdown for connecting an entity to a subsidiary. No selection means the
 * entity belongs to the organisation directly — the empty-key "Inget
 * dotterbolag" option (matching the placeholder wording) clears back to that,
 * mapping to an undefined subsidiaryId on submit.
 */
const SubsidiarySelect = ({
	control,
	name,
	subsidiaries,
	margin,
	mandatory,
}: SubsidiarySelectProps) => {
	const { t } = useTranslation();

	const options: Option[] = [
		{ key: "", name: t("NO_SUBSIDIARY") },
		...subsidiaries.map((s) => ({ key: s.id, name: s.name })),
	];

	return (
		<DropdownController<Option>
			control={control}
			label="BELONGS_TO_SUBSIDIARY"
			name={name}
			options={options}
			getOptionLabel={(o) => o.name}
			getValue={(o) => o.key}
			placeholder="NO_SUBSIDIARY"
			margin={margin}
			mandatory={mandatory}
		/>
	);
};

export default SubsidiarySelect;

/**
 * Resolves which organisation pools are selectable given the currently selected
 * subsidiary. A subsidiary with its own pools constrains the list to that
 * subset; otherwise (or "organisation directly") all organisation pools apply.
 *
 * Deactivated pools are excluded, except the entity's currently assigned pool
 * (`currentPool`) which stays selectable so existing profiles remain saveable.
 */
export function useAvailablePools(
	control: Control<any, any>,
	subsidiaryFieldName: string,
	subsidiaries: Subsidiary[],
	orgPools: Pool[] = [],
	currentPool?: string
): Pool[] {
	const selectedId = useWatch({ control, name: subsidiaryFieldName }) as string | undefined;

	const selectablePools = orgPools.filter((p) => !p.deactivated || p.key === currentPool);

	if (!selectedId) return selectablePools;

	const selected = subsidiaries.find((s) => s.id === selectedId);

	if (selected?.pools?.length)
		return selectablePools.filter((p) => selected.pools!.includes(p.key));

	return selectablePools;
}
